import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import Interpreters from "@/pages/Interpreters";
import Projects from "@/pages/Projects";
import Translations from "@/pages/Translations";
import DeliveryDownload from "@/pages/DeliveryDownload";
import Contracts from "@/pages/Contracts";
import Communications from "@/pages/Communications";
import CalendarPage from "@/pages/CalendarPage";
import Invoices from "@/pages/Invoices";
import AIAssistant from "@/pages/AIAssistant";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";
import Reports from "@/pages/Reports";
import ClientDetail from "@/pages/ClientDetail";
import InterpreterDetail from "@/pages/InterpreterDetail";
import ApplyInterpreter from "@/pages/ApplyInterpreter";
import ClientIntake from "@/pages/ClientIntake";
import SignInterpreterAgreement from "@/pages/SignInterpreterAgreement";
import SignClientAgreement from "@/pages/SignClientAgreement";
import InterpreterInquiryRespond from "@/pages/InterpreterInquiryRespond";
import PortalLogin from "@/pages/PortalLogin";
import PortalVerify from "@/pages/PortalVerify";
import PortalDashboard from "@/pages/PortalDashboard";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import PendingApplications from "@/pages/PendingApplications";
import PendingClients from "@/pages/PendingClients";
import Payments from "@/pages/Payments";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Shell from "@/components/Shell";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }
  if (!user) {
    // Preserve where they were trying to go so we can send them back after re-login,
    // and surface a "session expired" toast on the login page.
    const returnTo = location.pathname + location.search;
    return <Navigate to={`/?expired=1&returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  return <Shell>{children}</Shell>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/apply" element={<ApplyInterpreter />} />
      <Route path="/client-intake" element={<ClientIntake />} />
      <Route path="/client-intake/:token" element={<ClientIntake />} />
      <Route path="/sign/interpreter/:token" element={<SignInterpreterAgreement />} />
      <Route path="/sign/client/:token" element={<SignClientAgreement />} />
      <Route path="/delivery/:token" element={<DeliveryDownload />} />
      <Route path="/interpreter-inquiry/:token" element={<InterpreterInquiryRespond />} />
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/verify" element={<PortalVerify />} />
      <Route path="/portal" element={<PortalDashboard />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/clients" element={<Protected><Clients /></Protected>} />
      <Route path="/clients/:id" element={<Protected><ClientDetail /></Protected>} />
      <Route path="/interpreters" element={<Protected><Interpreters /></Protected>} />
      <Route path="/interpreters/:id" element={<Protected><InterpreterDetail /></Protected>} />
      <Route path="/projects" element={<Protected><Projects /></Protected>} />
      <Route path="/translations" element={<Protected><Translations /></Protected>} />
      <Route path="/contracts" element={<Protected><Contracts /></Protected>} />
      <Route path="/communications" element={<Protected><Communications /></Protected>} />
      <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
      <Route path="/ai" element={<Protected><AIAssistant /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/payments" element={<Protected><Payments /></Protected>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/team" element={<Protected><Team /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/pending-applications" element={<Protected><PendingApplications /></Protected>} />
      <Route path="/pending-clients" element={<Protected><PendingClients /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
